import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Search, Users, Plus, Send, Paperclip, Image as ImageIcon, User, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useConversations, useMessages, useCreateConversation } from '@/hooks/useChats';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import type { Conversation, Message } from '@/types/chat';

interface Employee {
  id: string;
  name: string;
  profile_image?: string;
  position: string;
}

const MessagesPage = () => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentEmployeeId = localStorage.getItem('currentEmployeeId');

  const { conversations, isLoading: conversationsLoading } = useConversations();
  const { messages, sendMessage, isSending } = useMessages(selectedConversation?.id || '');
  const createConversation = useCreateConversation();

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-list'],
    queryFn: async (): Promise<Employee[]> => {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name, profile_image, position')
        .eq('approval_status', 'approved')
        .neq('id', currentEmployeeId || '')
        .order('name');
      
      if (error) throw error;
      return data || [];
    }
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    sendMessage({
      content: newMessage.trim(),
      message_type: 'text'
    });
    
    setNewMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateConversation = async () => {
    if (selectedEmployees.length === 0) return;
    
    const result = await createConversation.mutateAsync({
      participantIds: selectedEmployees,
      title: selectedEmployees.length > 1 ? 'محادثة جماعية' : undefined
    });
    
    if (result) {
      setIsNewChatOpen(false);
      setSelectedEmployees([]);
      // Select the new conversation
      const newConv = conversations.find(c => c.id === result.id);
      if (newConv) setSelectedConversation(newConv);
    }
  };

  const formatTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: ar
    });
  };

  const getConversationTitle = (conversation: Conversation) => {
    if (conversation.title) return conversation.title;
    
    const otherParticipants = conversation.participants?.filter(
      p => p.employee_id !== currentEmployeeId
    ) || [];
    
    if (otherParticipants.length === 1) {
      return otherParticipants[0].employee?.name || 'محادثة';
    }
    
    return `محادثة جماعية (${otherParticipants.length + 1})`;
  };

  const filteredConversations = conversations.filter(conv => 
    getConversationTitle(conv).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-screen bg-background flex" dir="rtl">
      {/* Sidebar - Conversations List */}
      <div className="w-80 border-l bg-card flex flex-col">
        {/* Header */}
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-primary" />
              <CardTitle>المحادثات</CardTitle>
            </div>
            <Dialog open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  محادثة جديدة
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>إنشاء محادثة جديدة</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">اختر الموظفين:</label>
                    <ScrollArea className="h-60 mt-2 border rounded-md p-2">
                      {employees.map(employee => (
                        <div
                          key={employee.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-accent ${
                            selectedEmployees.includes(employee.id) ? 'bg-accent' : ''
                          }`}
                          onClick={() => {
                            setSelectedEmployees(prev => 
                              prev.includes(employee.id)
                                ? prev.filter(id => id !== employee.id)
                                : [...prev, employee.id]
                            );
                          }}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={employee.profile_image} />
                            <AvatarFallback>{employee.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{employee.name}</p>
                            <p className="text-xs text-muted-foreground">{employee.position}</p>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                  <Button 
                    onClick={handleCreateConversation}
                    disabled={selectedEmployees.length === 0 || createConversation.isPending}
                    className="w-full"
                  >
                    إنشاء المحادثة
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="البحث في المحادثات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10"
            />
          </div>
        </CardHeader>

        {/* Conversations List */}
        <ScrollArea className="flex-1">
          <div className="space-y-1 p-2">
            {conversationsLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                جاري تحميل المحادثات...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                لا توجد محادثات
              </div>
            ) : (
              filteredConversations.map(conversation => (
                <div
                  key={conversation.id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-accent ${
                    selectedConversation?.id === conversation.id ? 'bg-accent' : ''
                  }`}
                  onClick={() => setSelectedConversation(conversation)}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback>
                        {conversation.is_group ? (
                          <Users className="w-5 h-5" />
                        ) : (
                          getConversationTitle(conversation).charAt(0)
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium truncate">
                          {getConversationTitle(conversation)}
                        </h3>
                        {conversation.last_message_at && (
                          <span className="text-xs text-muted-foreground">
                            {formatTime(conversation.last_message_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        آخر نشاط: {conversation.last_message_at ? formatTime(conversation.last_message_at) : 'لا يوجد'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="border-b bg-card p-4">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback>
                    {selectedConversation.is_group ? (
                      <Users className="w-5 h-5" />
                    ) : (
                      getConversationTitle(selectedConversation).charAt(0)
                    )}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="font-semibold">{getConversationTitle(selectedConversation)}</h2>
                  <p className="text-sm text-muted-foreground">
                    {selectedConversation.participants?.length || 0} مشارك
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${
                      message.sender_id === currentEmployeeId ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={message.sender?.profile_image} />
                      <AvatarFallback>
                        {message.sender?.name?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`flex flex-col ${
                      message.sender_id === currentEmployeeId ? 'items-end' : 'items-start'
                    }`}>
                      <div className={`max-w-md p-3 rounded-lg ${
                        message.sender_id === currentEmployeeId
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                      </div>
                      <span className="text-xs text-muted-foreground mt-1">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="border-t bg-card p-4">
              <div className="flex gap-2">
                <Button variant="outline" size="icon">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon">
                  <ImageIcon className="w-4 h-4" />
                </Button>
                <Textarea
                  placeholder="اكتب رسالتك..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="min-h-[60px] resize-none"
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSending}
                  size="icon"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">مرحباً بك في المحادثات</h3>
              <p className="text-muted-foreground">اختر محادثة للبدء أو أنشئ محادثة جديدة</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;